import { useFormik } from "formik";
import * as Yup from "yup";
import { format, isBefore } from "date-fns";
import { BeskikAdvokatDTO } from "../BeskikketAdvokat/useBeskikketAdvokat";
import { useMutation } from "@tanstack/react-query";
import { patch, post } from "../../../api/util";
import { PersonDto } from "../../../interfaces/schema";
import { getPatchOperations } from "../../../util/form";
import {
  cprLoebeNrSchema,
  stripCprRestrictedChanges,
} from "../../../util/cprAccess";
import { dateIsWithinInterval } from "../../../util/date";
import { logSafeError } from "../../../util/logSafeError";

export function useBeskikDeponerAdvokat(
  person: PersonDto,
  cpr: string,
  canReadCpr: boolean,
) {
  const {
    mutateAsync: patchPersonMutation,
    error: patchError,
    reset: resetPersonQuery,
  } = useMutation({
    mutationFn: async (body: {
      foedselsDato?: string;
      CprLoebeNr?: string;
    }) => {
      return await patch({
        query: `person/${person.id}`,
        body: JSON.stringify(getPatchOperations(body)),
      });
    },
  });
  const initialValues = {
    foedselsDato: person.foedselsDato
      ? new Date(person.foedselsDato)
      : undefined,
    cprNr: cpr ? cpr.split("-")[1] : "",
    dato: undefined,
    aarsag: "",
  };
  const beskikDeponerValidation = useFormik<BeskikDeponerAdvokatFormik>({
    validateOnMount: true,
    initialValues,
    validationSchema: Yup.object({
      dato: Yup.date().nullable().required("Dato skal være sat"),
      aarsag: Yup.string(),
      foedselsDato: Yup.date()
        .nullable()
        .required("Fødselsdato skal være sat")
        .test("foedselsDato", "Årstal skal være mindst 1900", (item) => {
          if (!item) return true;
          return item.getFullYear() > 1899;
        })
        .test("foedselsDato", "Må ikke være i fremtiden", (item) => {
          if (!item) return true;
          const date = new Date(item);
          return isBefore(date, new Date());
        }),
      cprNr: cprLoebeNrSchema(canReadCpr),
    }),
    onSubmit: async (values) => {
      const { aarsag, dato, foedselsDato, cprNr } = values;
      try {
        if (!!foedselsDato && foedselsDato !== initialValues.foedselsDato) {
          // ADV-349: uden CprRead sendes CprLoebeNr aldrig, og fødselsdatoen ikke for en
          // person med CPR-nummer — backenden ville afvise PATCH'en.
          const patchBody = stripCprRestrictedChanges(
            {
              foedselsDato: foedselsDato
                ? format(foedselsDato, "yyyy-MM-dd")
                : "",
              CprLoebeNr: cprNr,
            },
            {
              canReadCpr,
              harCprNummer: !!person.harCprNummer,
              foedselsDato: person.foedselsDato,
            },
          );
          if (Object.keys(patchBody).length > 0) {
            await patchPersonMutation(patchBody);
          }
        }
        const aktiveFirmaer = person.personFirma?.filter((pf) =>
          dato
            ? dateIsWithinInterval(
                dato,
                new Date(pf.periode.fraDato),
                new Date(pf.periode.tilDatoInklusiv),
              )
            : false,
        );
        const futureFirmaer =
          dato && person.personFirma
            ? person.personFirma.filter(
                (element) => new Date(element?.periode.fraDato) > dato,
              )
            : [];

        const dto: BeskikAdvokatDTO = {
          straksDeponering: true,
          fraDato: dato ? format(dato, "yyyy-MM-dd") : "",
          aarsag,
          ophoerendePersonFirmarelationer: aktiveFirmaer?.map((f) => f.id),
          fortsaettendePersonFirmarelationer: futureFirmaer.map((f) => f.id),
        };
        await beskikDeponeringQuery.mutateAsync(dto);
      } catch (error) {
        logSafeError(error);
      }
    },
  });
  const beskikDeponeringQuery = useMutation({
    mutationFn: (dto: BeskikAdvokatDTO) => {
      return post<{ id: string; fornavne: string; efternavn: string }>({
        query: `person/${person.id}/wizards/beskikadvokat`,
        body: dto,
      });
    },
  });
  const dato = beskikDeponerValidation.values.dato;
  const aktiveFirmaRelationer =
    person.personFirma?.filter((pf) =>
      dato
        ? dateIsWithinInterval(
            dato,
            new Date(pf.periode.fraDato),
            new Date(pf.periode.tilDatoInklusiv),
          )
        : false,
    ) ?? [];
  const futureFirmaRelationer =
    dato && person.personFirma
      ? person.personFirma.filter(
          (element) => new Date(element?.periode.fraDato) > dato,
        )
      : [];
  return {
    futureFirmaRelationer,
    aktiveFirmaRelationer,
    formik: beskikDeponerValidation,
    beskikDeponeringQuery,
    patchError,
    resetPersonQuery,
  };
}

export type BeskikDeponerAdvokatFormik = {
  foedselsDato: Date | undefined;
  cprNr: string;
  dato: Date | undefined;
  aarsag: string;
};
